import React, { useState, useEffect, useRef } from 'react';
import { ShieldAlert, LineChart, Home, Scale, Check, Pencil, Paperclip, LoaderCircle, FileText, Download, Eye } from 'lucide-react';
import { updateDossier } from '../../api/forumAPI';
import { uploadDocument } from '../../../documents/api/documentsAPI';
import { useNotification } from '../../../../context/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
import Tooltip from '../../../../components/ui/Tooltip';
import emailjs from '@emailjs/browser';
import api from '../../../../api';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

const AnalysisField = ({ title, icon: Icon, name, value, isEditing, onEdit, onChange, placeholder, extraContent, customClass = "", fileData, onFileUpload, isUploading, isFrozen }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      onFileUpload(e.target.files[0], name);
    }
  };

  return (
    <motion.div variants={itemVariants} className={`p-5 rounded-xl border flex flex-col h-full transition-colors ${customClass}`}>
      <div className="flex justify-between items-start mb-4">
        <label className="flex items-center text-sm font-bold text-primary">
          <Icon className="w-5 h-5 mr-2 text-tertiary" /> {title}
        </label>
        {!isEditing && !isFrozen && (
          <Tooltip content="Modifier">
            <button onClick={onEdit} className="text-tertiary hover:text-primary bg-white/50 rounded p-1.5 transition-colors shadow-sm">
              <Pencil className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
      </div>
      
      <div className="flex-grow flex flex-col gap-3">
        {isEditing ? (
          <div>
            <textarea 
              name={name}
              rows={6} 
              value={value}
              onChange={onChange}
              className="workspace-input w-full bg-surface resize-y" 
              placeholder={placeholder}
              maxLength={2000}
            />
            <div className="text-right text-xs text-tertiary mt-1">
              {(value || '').length}/2000
            </div>
          </div>
        ) : (
          <div className="text-sm text-secondary whitespace-pre-wrap leading-relaxed">
            {value || <span className="italic text-tertiary">Non renseigné</span>}
          </div>
        )}

        {/* File Attachment Section */}
        {fileData && (
          <div className="mt-2 p-3 bg-surface rounded-lg border border-workspace flex items-center justify-between">
            <div className="flex items-center space-x-3 overflow-hidden">
              <FileText className="w-5 h-5 text-primary shrink-0" />
              <span className="text-sm text-secondary truncate" title={fileData.name}>{fileData.name}</span>
            </div>
            <a href={fileData.url?.startsWith('/api') ? fileData.url : `/api${fileData.url?.startsWith('/') ? fileData.url : `/${fileData.url}`}`} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-surface-muted hover:bg-surface rounded text-primary shrink-0 transition-colors">
              <Eye className="w-4 h-4" />
            </a>
          </div>
        )}

        {isEditing && (
          <div className="mt-2">
            <input 
              type="file" 
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept=".doc,.docx,.pdf"
            />
            <button 
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="flex items-center text-sm text-tertiary hover:text-primary transition-colors disabled:opacity-50"
            >
              {isUploading ? <LoaderCircle className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}
              {isUploading ? "Upload en cours..." : (fileData ? "Remplacer le fichier" : "Joindre un fichier (Word, PDF)")}
            </button>
          </div>
        )}
      </div>
      
      {extraContent}
    </motion.div>
  );
};

export default function DossierAnalyses({ dossier, onRefresh }) {
 const { showToast } = useNotification();
 const [loading, setLoading] = useState(false);
 
 const initialAnalyses = dossier.analyses || {};
 const isFrozen = !['NEW', 'TO_QUALIFY', 'DOCUMENTS_PENDING', 'UNDER_ANALYSIS'].includes(dossier.status);
 const [formData, setFormData] = useState({
   financialAnalysis: initialAnalyses.financialAnalysis || '',
   financialAnalysisFile: initialAnalyses.financialAnalysisFile || null,
   realEstateAnalysis: initialAnalyses.realEstateAnalysis || '',
   realEstateAnalysisFile: initialAnalyses.realEstateAnalysisFile || null,
   legalAnalysis: initialAnalyses.legalAnalysis || '',
   legalAnalysisFile: initialAnalyses.legalAnalysisFile || null,
   legalValidationRequested: initialAnalyses.legalValidationRequested || false,
   legalValidationAssignedTo: initialAnalyses.legalValidationAssignedTo || ''
 });

 const [avocatOptions, setAvocatOptions] = useState([]);

 const [editModes, setEditModes] = useState({
   financial: !isFrozen && !initialAnalyses.financialAnalysis,
   realEstate: !isFrozen && !initialAnalyses.realEstateAnalysis,
   legal: !isFrozen && !initialAnalyses.legalAnalysis
 });

 const [uploadingStates, setUploadingStates] = useState({
   financialAnalysis: false,
   realEstateAnalysis: false,
   legalAnalysis: false
 });

 useEffect(() => {
   const updatedAnalyses = dossier.analyses || {};
   setFormData({
     financialAnalysis: updatedAnalyses.financialAnalysis || '',
     financialAnalysisFile: updatedAnalyses.financialAnalysisFile || null,
     realEstateAnalysis: updatedAnalyses.realEstateAnalysis || '',
     realEstateAnalysisFile: updatedAnalyses.realEstateAnalysisFile || null,
     legalAnalysis: updatedAnalyses.legalAnalysis || '',
     legalAnalysisFile: updatedAnalyses.legalAnalysisFile || null,
     legalValidationRequested: updatedAnalyses.legalValidationRequested || false,
     legalValidationAssignedTo: updatedAnalyses.legalValidationAssignedTo || ''
   });
   setEditModes({
     financial: !isFrozen && !updatedAnalyses.financialAnalysis,
     realEstate: !isFrozen && !updatedAnalyses.realEstateAnalysis,
     legal: !isFrozen && !updatedAnalyses.legalAnalysis
   });
 }, [dossier, isFrozen]);

 useEffect(() => {
   const fetchAvocats = async () => {
     try {
       const res = await api.get('/utilisateurs/members');
       const avocats = res.data.filter(m => m.Role && m.Role.toLowerCase().includes('avocat'));
       setAvocatOptions(avocats.map(m => ({ label: `${m.Nom} (${m.Role})`, value: m.id, email: m.Email })));
     } catch (err) {
       console.error("Erreur chargement avocats:", err);
     }
   };
   fetchAvocats();
 }, []);

 const handleChange = (e) => {
   const { name, value, type, checked } = e.target;
   setFormData(prev => ({ 
     ...prev, 
     [name]: type === 'checkbox' ? checked : value 
   }));
 };

 const handleFileUpload = async (file, name) => {
   setUploadingStates(prev => ({ ...prev, [name]: true }));
   try {
     const uploadData = new FormData();
     uploadData.append('file', file);
     uploadData.append('operationCaseId', dossier.id);
     uploadData.append('title', file.name);

     const doc = await uploadDocument(uploadData);
     const fileData = {
       id: doc.id,
       name: doc.originalFilename || file.name,
       url: doc.storageKey || doc.fileUrl || doc.url
     };
     setFormData(prev => ({ ...prev, [`${name}File`]: fileData }));
     showToast("Fichier uploadé avec succès", "success");
   } catch (error) {
     console.error("Upload error", error);
     showToast("Erreur lors de l'upload du fichier", "error");
   } finally {
     setUploadingStates(prev => ({ ...prev, [name]: false }));
   }
 };

 const toggleEdit = (field) => {
   if (isFrozen) return;
   setEditModes(prev => ({ ...prev, [field]: !prev[field] }));
 };

 const handleSave = async () => {
   setLoading(true);
   try {
     await updateDossier(dossier.id, { analyses: formData });
     showToast("Analyses enregistrées avec succès", "success");
     
     if (formData.legalValidationRequested && formData.legalValidationAssignedTo && formData.legalValidationAssignedTo !== initialAnalyses.legalValidationAssignedTo) {
       const chosenLawyer = avocatOptions.find(opt => opt.value === formData.legalValidationAssignedTo);
       if (chosenLawyer) {
          try {
            await api.post('/tasks', {
              title: `Validation juridique requise - ${dossier.name || dossier.reference || 'Dossier'}`,
              description: `Merci de bien vouloir analyser et valider les aspects juridiques de ce dossier.`,
              status: 'TODO',
              priority: 'HIGH',
              assignedMembershipId: chosenLawyer.value,
              operationCaseId: dossier.id
            });
          } catch(taskErr) {
            console.error("Erreur création tâche:", taskErr);
          }
          
          if (chosenLawyer.email) {
            const dossierLink = `${window.location.origin}/forum/dossiers/${dossier.id}`;
            emailjs.send(
              process.env.REACT_APP_EMAILJS_SERVICE_ID || "default_service",
              process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "default_template",
              {
                to_email: chosenLawyer.email,
                to_name: chosenLawyer.label,
                dossier_title: dossier.name || dossier.reference || 'Dossier',
                dossier_link: dossierLink,
              },
              process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "default_public_key"
            ).catch(emailErr => console.error("EmailJS Error:", emailErr));
            showToast("L'avocat a été notifié avec succès", "success");
          }
       }
     }

     setEditModes({
       financial: !formData.financialAnalysis,
       realEstate: !formData.realEstateAnalysis,
       legal: !formData.legalAnalysis
     });

     if(onRefresh) onRefresh();
   } catch(err) {
     console.error("Erreur lors de la sauvegarde des analyses", err);
     showToast("Erreur lors de la sauvegarde des analyses", "error");
   } finally {
     setLoading(false);
   }
 };

 const isLegalRequested = formData.legalValidationRequested;
 const legalClass = isLegalRequested 
   ? "bg-orange-50 border-orange-300 shadow-md" 
   : "bg-surface-muted border-workspace";

 const formattedDate = dossier.updatedAt ? new Date(dossier.updatedAt).toLocaleString('fr-FR', {
   day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
 }) : '';

 return (
 <div className="workspace-card p-6">
   <div className="mb-6 flex justify-between items-start">
     <div>
       <h3 className="text-lg font-bold text-primary mb-1 flex items-center gap-2">
         <ShieldAlert className="w-5 h-5 text-primary" />
         Analyses & Risques
       </h3>
       <p className="text-sm text-tertiary">Zone de rédaction pour les analyses juridiques, financières et immobilières.</p>
     </div>
     {isFrozen && formattedDate && (
       <div className="bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-blue-200 flex items-center">
         Dernière mise à jour le {formattedDate}
       </div>
     )}
   </div>
   
   <motion.div 
     variants={containerVariants}
     initial="hidden"
     animate="visible"
     className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch"
   >
     <AnalysisField 
        title="Analyse financière"
        icon={LineChart}
        name="financialAnalysis"
        value={formData.financialAnalysis}
        isEditing={editModes.financial}
        onEdit={() => toggleEdit('financial')}
        onChange={handleChange}
        placeholder="Saisir l'analyse financière..."
        customClass="bg-surface-muted border-workspace"
        fileData={formData.financialAnalysisFile}
        onFileUpload={handleFileUpload}
        isUploading={uploadingStates.financialAnalysis}
        isFrozen={isFrozen}
     />

     <AnalysisField 
        title="Analyse collatéral"
        icon={Home}
        name="realEstateAnalysis"
        value={formData.realEstateAnalysis}
        isEditing={editModes.realEstate}
        onEdit={() => toggleEdit('realEstate')}
        onChange={handleChange}
        placeholder="Saisir l'analyse collatéral..."
        customClass="bg-surface-muted border-workspace"
        fileData={formData.realEstateAnalysisFile}
        onFileUpload={handleFileUpload}
        isUploading={uploadingStates.realEstateAnalysis}
        isFrozen={isFrozen}
     />

     <AnalysisField 
        title="Avis juridique"
        icon={Scale}
        name="legalAnalysis"
        value={formData.legalAnalysis}
        isEditing={editModes.legal}
        onEdit={() => toggleEdit('legal')}
        onChange={handleChange}
        placeholder="Saisir l'avis juridique..."
        customClass={legalClass}
        fileData={formData.legalAnalysisFile}
        onFileUpload={handleFileUpload}
        isUploading={uploadingStates.legalAnalysis}
        isFrozen={isFrozen}
        extraContent={
          <div className={`mt-4 pt-4 border-t flex flex-col gap-3 transition-colors ${isLegalRequested ? 'border-orange-200' : 'border-workspace'}`}>
             <div className="flex items-center">
               <input
                 type="checkbox"
                 id="legalValidationRequested"
                 name="legalValidationRequested"
                 checked={formData.legalValidationRequested}
                 onChange={handleChange}
                 disabled={isFrozen && !editModes.legal}
                 className="w-4 h-4 rounded border-ink-300 text-orange-500 focus:ring-orange-500 cursor-pointer disabled:opacity-50"
               />
               <label htmlFor="legalValidationRequested" className={`ml-2 text-sm font-semibold cursor-pointer ${isLegalRequested ? 'text-orange-700' : 'text-secondary'}`}>
                 Demande de validation par avocat
               </label>
             </div>
             <AnimatePresence>
             {isLegalRequested && (
               <motion.div 
                 initial={{ opacity: 0, height: 0 }}
                 animate={{ opacity: 1, height: 'auto' }}
                 exit={{ opacity: 0, height: 0 }}
                 className="pl-6 overflow-hidden"
               >
                 <label className="block text-xs font-semibold text-tertiary mb-1">Sélectionner l'avocat</label>
                 <select
                   name="legalValidationAssignedTo"
                   value={formData.legalValidationAssignedTo}
                   onChange={handleChange}
                   disabled={isFrozen && !editModes.legal}
                   className="workspace-input w-full text-sm py-1.5"
                 >
                   <option value="">Sélectionnez un avocat...</option>
                   {avocatOptions.map(opt => (
                     <option key={opt.value} value={opt.value}>{opt.label}</option>
                   ))}
                 </select>
               </motion.div>
             )}
             </AnimatePresence>
           </div>
        }
     />
   </motion.div>
   
   {!isFrozen && (
   <div className="flex justify-end pt-6 mt-6 border-t border-workspace">
     <button 
       onClick={handleSave}
       disabled={loading}
       className="btn btn-primary flex items-center shadow-hp-sm hover:shadow-hp-md"
     >
       {loading ? <LoaderCircle className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
       {loading ? "Enregistrement..." : "Enregistrer les analyses"}
     </button>
   </div>
   )}
 </div>
 );
}

